/**
 * CersFacilityMatchDialog — staff UI to confirm a suggested match, search
 * globally for an Encamp facility to link, or override an existing match.
 *
 * Data display mirrors the matching grid exactly (shared MatchCells): the CERS
 * block at top renders like the grid's CERS cell, and every Encamp candidate
 * (suggestion / address match / search result) renders like the grid's Encamp
 * cell — so staff see the same block format they already know.
 *
 * Three flows:
 *  1. Suggested  — show candidate list; one click confirms and calls
 *                  linkCersFacility.
 *  2. Manual     — freeform search across all tenants; pick a row to link.
 *  3. Override   — when already MATCHED_CERS, show a confirmation prompt before
 *                  re-linking so staff can't accidentally clobber a good match.
 */

import { useMutation, useQuery } from "@apollo/client";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import Search from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { StatusChip } from "components/StatusChip";
import {
  CersFacilityMatchesQuery,
  CersFacilityMatchStatus,
  CersFacilitySearchQuery,
} from "generated-graphql/graphql";
import { useDebounce } from "hooks/useDebounce";
import { useCallback, useState } from "react";

import {
  CERS_FACILITY_ADDRESS_MATCHES,
  CERS_FACILITY_SEARCH,
  LINK_CERS_FACILITY,
  SET_CERS_FACILITY_IGNORED,
} from "./api";
import { IgnoreReasonDialog } from "./IgnoreReasonDialog";
import { CandidateFacilityCell, CersFacilityCell } from "./MatchCells";
import { confidenceDisplay, matchBasisLabel, matchedFields } from "./matchRow";

// ─── types ────────────────────────────────────────────────────────────────────

type CersFacilityRow = CersFacilityMatchesQuery["cersFacilityMatches"]["items"][number];
type SearchResultRow = CersFacilitySearchQuery["facilities"]["items"][number];

interface Props {
  open: boolean;
  onClose: () => void;
  cersFacility: CersFacilityRow | null;
}

// ─── component ────────────────────────────────────────────────────────────────

export function CersFacilityMatchDialog({ open, onClose, cersFacility }: Props) {
  const alerts = useAlerts();

  // Manual search
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch] = useDebounce(searchInput, 400);

  // Override confirmation: when the row is already MATCHED_CERS we ask the
  // user to confirm before re-linking. pendingLinkId holds the encampFacilityId
  // the user chose; once they confirm we call link() without guard.
  const [pendingLinkId, setPendingLinkId] = useState<string | null>(null);
  const [pendingLinkName, setPendingLinkName] = useState<string | null>(null);

  const { data: searchData, loading: searchLoading } = useQuery(CERS_FACILITY_SEARCH, {
    variables: { search: debouncedSearch, page: 0, pageSize: 25 },
    skip: !open || !debouncedSearch,
    fetchPolicy: "cache-and-network",
  });

  // On-demand normalized-fuzzy address suggestions: Encamp facilities whose
  // street address is similar (pg_trgm) to the CERS facility's site address.
  const { data: addressMatchData } = useQuery(CERS_FACILITY_ADDRESS_MATCHES, {
    variables: { cersFacilityId: cersFacility?.cersId ?? "" },
    skip: !open || !cersFacility,
  });

  const [linkCersFacility, { loading: linking }] = useMutation(LINK_CERS_FACILITY, {
    refetchQueries: ["CersFacilityMatches", "CersFacilityMatchStats"],
    awaitRefetchQueries: true,
  });

  const [setCersFacilityIgnored, { loading: ignoring }] = useMutation(SET_CERS_FACILITY_IGNORED, {
    refetchQueries: ["CersFacilityMatches", "CersFacilityMatchStats"],
    awaitRefetchQueries: true,
  });

  // Derived values — safe to compute even when cersFacility is null.
  const isAlreadyMatched = cersFacility?.matchStatus === CersFacilityMatchStatus.MatchedCers;
  const suggestions = cersFacility?.suggestions ?? [];
  const searchFacilities = searchData?.facilities?.items ?? [];
  const addressMatches = addressMatchData?.cersFacilityAddressMatches ?? [];

  // Ignore reason entry state
  const [enteringIgnoreReason, setEnteringIgnoreReason] = useState(false);

  const handleClose = useCallback(() => {
    setSearchInput("");
    setPendingLinkId(null);
    setPendingLinkName(null);
    setEnteringIgnoreReason(false);
    onClose();
  }, [onClose]);

  const performLink = useCallback(
    async (encampFacilityId: string, encampFacilityName: string) => {
      if (!cersFacility) return;

      // Show the override confirmation only when re-linking to a DIFFERENT
      // facility (re-confirming the already-linked one is a harmless no-op, no
      // "move the match" prompt needed).
      if (
        isAlreadyMatched &&
        encampFacilityId !== cersFacility.encampFacility?.id &&
        pendingLinkId !== encampFacilityId
      ) {
        setPendingLinkId(encampFacilityId);
        setPendingLinkName(encampFacilityName);
        return;
      }

      try {
        await linkCersFacility({
          variables: {
            cersFacilityId: cersFacility.cersId,
            encampFacilityId,
          },
        });
        alerts.success(
          `Linked CERS facility "${cersFacility.name ?? cersFacility.cersId}" to "${encampFacilityName}"`
        );
        handleClose();
      } catch (err) {
        alerts.error(`Failed to link: ${err instanceof Error ? err.message : String(err)}`, err);
      }
    },
    [cersFacility, isAlreadyMatched, pendingLinkId, linkCersFacility, alerts, handleClose]
  );

  const handleSetIgnored = useCallback(
    async (ignored: boolean, reason?: string) => {
      if (!cersFacility) return;
      try {
        await setCersFacilityIgnored({
          variables: { cersFacilityId: cersFacility.cersId, ignored, reason },
        });
        const label = cersFacility.name ?? cersFacility.cersId;
        alerts.success(
          ignored ? `Ignored CERS facility "${label}"` : `Un-ignored CERS facility "${label}"`
        );
        handleClose();
      } catch (err) {
        alerts.error(`Failed to update: ${err instanceof Error ? err.message : String(err)}`, err);
      }
    },
    [cersFacility, setCersFacilityIgnored, alerts, handleClose]
  );

  function confirmOverride() {
    if (!pendingLinkId || !pendingLinkName) return;
    const id = pendingLinkId;
    const name = pendingLinkName;
    // Clear pending state first so the guard in performLink won't re-enter.
    setPendingLinkId(null);
    setPendingLinkName(null);
    void performLink(id, name);
  }

  function cancelOverride() {
    setPendingLinkId(null);
    setPendingLinkName(null);
  }

  if (!cersFacility) return null;

  // ── override confirmation view ─────────────────────────────────────────────
  if (pendingLinkId) {
    const currentName = cersFacility.encampFacility?.name ?? "unknown";
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        title="Confirm override"
        noPrimaryAction
        hideCancelButton
      >
        <Stack spacing={3} sx={{ pb: 1 }}>
          <Typography>
            This CERS facility is currently matched to <strong>{currentName}</strong>. Move the
            match to <strong>{pendingLinkName}</strong>?
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={cancelOverride} disabled={linking}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={confirmOverride}
              disabled={linking}
              startIcon={linking ? <CircularProgress size={16} /> : undefined}
            >
              {linking ? "Linking…" : "Confirm"}
            </Button>
          </Stack>
        </Stack>
      </Dialog>
    );
  }

  // ── ignore reason entry view ───────────────────────────────────────────────
  // Reuse the shared IgnoreReasonDialog (also used by the Facilities grid).
  // Cancel / dismiss returns to the match view; confirm ignores with the reason.
  if (enteringIgnoreReason) {
    return (
      <IgnoreReasonDialog
        open={open}
        facilityName={cersFacility.name ?? null}
        loading={ignoring}
        onClose={() => setEnteringIgnoreReason(false)}
        onConfirm={(reason) => void handleSetIgnored(true, reason)}
      />
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      title="Match CERS Facility"
      noPrimaryAction
      hideCancelButton
    >
      <Stack spacing={3} sx={{ pb: 3 }}>
        {/* ── header: CERS facility — pinned to the top so it stays visible while
            the suggestions / search results scroll beneath it ───────────────── */}
        <Box sx={{ position: "sticky", top: 0, zIndex: 2, bgcolor: "background.paper", pb: 2 }}>
          <Card variant="outlined">
            <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={1}
              >
                <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
                  CERS Facility
                </Typography>
                {/* Ignore / un-ignore is available here for ANY row (linked or not):
                    "not under Encamp contract" is orthogonal to match state. */}
                <Button
                  size="small"
                  variant={cersFacility.ignoredAt ? "contained" : "outlined"}
                  color="staff"
                  disabled={ignoring}
                  onClick={() => {
                    if (cersFacility.ignoredAt) {
                      void handleSetIgnored(false);
                    } else {
                      setEnteringIgnoreReason(true);
                    }
                  }}
                >
                  {cersFacility.ignoredAt ? "Un-ignore" : "Ignore"}
                </Button>
              </Stack>
              <CersFacilityCell row={cersFacility} />
              {cersFacility.ignoredAt && (
                <Box sx={{ mt: 1 }}>
                  <StatusChip color="default" label="Ignored — not under Encamp contract" />
                  {cersFacility.ignoreReason && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      Reason: {cersFacility.ignoreReason}
                    </Typography>
                  )}
                </Box>
              )}
              {isAlreadyMatched && cersFacility.encampFacility && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Currently matched to: <strong>{cersFacility.encampFacility.name}</strong>{" "}
                    <StatusChip color="success" label="Matched" />
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* ── suggestions (Encamp cell format + confidence + confirm) ───────── */}
        {suggestions.length > 0 && (
          <>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Suggested Matches ({suggestions.length})
              </Typography>
              <Stack spacing={1}>
                {suggestions.map((s) => {
                  const { color, label } = confidenceDisplay(s.confidence);
                  return (
                    <Card key={s.id} variant="outlined">
                      <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
                        {/* confidence + basis on top so the row below fits a skinny modal */}
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                          <StatusChip color={color} label={label} />
                          <Typography variant="caption" color="text.secondary">
                            {matchBasisLabel(s.source)}
                          </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="flex-start" spacing={2}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <CandidateFacilityCell
                              candidate={s.candidateFacility}
                              bold={matchedFields(cersFacility, s.candidateFacility)}
                            />
                          </Box>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<CheckCircleOutline fontSize="small" />}
                            disabled={linking}
                            onClick={() =>
                              void performLink(
                                s.candidateFacility.id,
                                s.candidateFacility.name ?? s.candidateFacility.id
                              )
                            }
                          >
                            Confirm
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            </Box>
            <Divider />
          </>
        )}

        {/* ── suggested address matches (Encamp cell format) ─────────────────── */}
        {addressMatches.length > 0 && (
          <>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Suggested Address Match ({addressMatches.length})
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Encamp facilities with a similar address.
              </Typography>
              <Stack spacing={1}>
                {addressMatches.map((facility) => (
                  <Card key={facility.id} variant="outlined">
                    <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <CandidateFacilityCell
                            candidate={facility}
                            bold={matchedFields(cersFacility, facility)}
                          />
                        </Box>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<CheckCircleOutline fontSize="small" />}
                          disabled={linking}
                          onClick={() =>
                            void performLink(facility.id, facility.name ?? facility.id)
                          }
                        >
                          Confirm
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
            <Divider />
          </>
        )}

        {/* ── manual search (results render as the Encamp cell format too) ───── */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Search Encamp Facilities
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Search by name, address, or EPA ID"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    {searchLoading ? <CircularProgress size={20} /> : <Search color="action" />}
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>

        {debouncedSearch && searchFacilities.length > 0 && (
          <Stack spacing={1}>
            {searchFacilities.map((facility: SearchResultRow) => (
              <Card key={facility.id} variant="outlined">
                <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* Only the street can bold here — CERS_FACILITY_SEARCH omits
                          epaIdStatuses, so matchedFields' epa axis is always false. */}
                      <CandidateFacilityCell
                        candidate={facility}
                        bold={matchedFields(cersFacility, facility)}
                      />
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={linking}
                      onClick={() => void performLink(facility.id, facility.name ?? facility.id)}
                    >
                      Link
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}

        {debouncedSearch && !searchLoading && searchFacilities.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No facilities found. Try adjusting the search.
          </Typography>
        )}

        {searchData?.facilities && searchData.facilities.count > searchFacilities.length && (
          <Typography variant="caption" color="text.secondary">
            Showing {searchFacilities.length} of {searchData.facilities.count.toLocaleString()}{" "}
            results
          </Typography>
        )}
      </Stack>
    </Dialog>
  );
}
